import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { calculateContainerAlgoPoints } from "@CommonUtils/pog/container";
import { resizeShelfDeadSpans, resizeShelfWidth } from "@CommonUtils/pog/shelfMerchandising";

import { getPegboardHoleCounts } from "../../common/pegboardHoleCounts";
import { moveShelvesInPog } from "../../store/actions";
import { projectPlanogramScene } from "./sceneProjection";
import { reconcileSelection } from "./selection";
import { emptyPlanogramSelection, type PlanogramPolicy, type PlanogramSelection } from "./types";
import type {
  ContainerMoveOperation,
  ContainerResizeOperation,
  ItemPlacementOperation,
  ItemRangeOperation,
  PegboardPlacementOperation,
  PlanogramMoveCapability,
  PlanogramMoveReasonCode,
  PlanogramMoveResult,
  PlanogramOperation,
  ShelfPlacementOperation,
  ShelfResizeOperation,
} from "./operations";

export type StandardMoveReasonCode = PlanogramMoveReasonCode;
export type StandardMoveCapability = PlanogramMoveCapability;
export type StandardMoveResult = PlanogramMoveResult;

const getShelfItem = (pog: Pog, itemId: string) => {
  const item = pog.getItem(itemId);
  return item instanceof PlanogramItem ? item : null;
};

const getPegboard = (pog: Pog, pegboardId: string) =>
  pog.planogram.bays
    .flatMap((bay) => bay.pegboards)
    .find((pegboard) => pegboard.uniqueId === pegboardId);

const getPegboardPlacementSources = (pog: Pog, operation: PegboardPlacementOperation) => {
  const sourceIds = new Set(operation.source.placementIds);
  return pog.planogram
    .getAllPegboardPlacements()
    .filter((placement) => sourceIds.has(placement.uniqueId));
};

const getPegboardHoleKey = (pegboardId: string, holeNoLeft: number, holeNoTop: number) =>
  `${pegboardId}:${holeNoLeft}:${holeNoTop}`;

const isPegboardPlacementNoOp = (
  pog: Pog,
  sources: ReturnType<typeof getPegboardPlacementSources>,
  target: NonNullable<ReturnType<typeof getPegboard>>,
  targetHole: PegboardPlacementOperation["target"],
  activeAnchor: { holeNoLeft: number; holeNoTop: number },
) => {
  const sourceIds = new Set(sources.map((source) => source.uniqueId));
  const leftDelta = targetHole.holeNoLeft - activeAnchor.holeNoLeft;
  const topDelta = targetHole.holeNoTop - activeAnchor.holeNoTop;
  const sourceStatesUnchanged = sources.every(
    (source) =>
      source.pegboard.uniqueId === target.uniqueId &&
      source.holes.every(
        (hole) =>
          hole.holeNoLeft + leftDelta === hole.holeNoLeft &&
          hole.holeNoTop + topDelta === hole.holeNoTop,
      ),
  );

  if (!sourceStatesUnchanged) return false;

  const projectedSourceIds = sources.map((source) => source.uniqueId);
  return pog.planogram.bays
    .flatMap((bay) => bay.pegboards)
    .every((pegboard) => {
      const currentIds = pegboard.placements.map((placement) => placement.uniqueId);
      const projectedIds = pegboard.placements
        .filter((placement) => !sourceIds.has(placement.uniqueId))
        .map((placement) => placement.uniqueId);
      if (pegboard.uniqueId === target.uniqueId) projectedIds.push(...projectedSourceIds);

      return (
        currentIds.length === projectedIds.length &&
        currentIds.every((placementId, index) => placementId === projectedIds[index])
      );
    });
};

export const evaluatePegboardPlacement = (
  pog: Pog,
  operation: PegboardPlacementOperation,
): PlanogramMoveCapability => {
  const sourceIds = operation.source.placementIds;
  if (sourceIds.length === 0) return { allowed: false, reason: "empty-source" };
  if (new Set(sourceIds).size !== sourceIds.length)
    return { allowed: false, reason: "duplicate-source" };
  if (!sourceIds.includes(operation.source.activePlacementId)) {
    return { allowed: false, reason: "unsupported-source-set" };
  }
  const sources = getPegboardPlacementSources(pog, operation);
  if (sources.length !== sourceIds.length) return { allowed: false, reason: "missing-source" };
  const target = getPegboard(pog, operation.target.pegboardId);
  if (!target) return { allowed: false, reason: "missing-target" };
  const active = sources.find(
    (placement) => placement.uniqueId === operation.source.activePlacementId,
  );
  const activeAnchor = active?.holes[0];
  if (!activeAnchor) return { allowed: false, reason: "invalid-hole" };
  const counts = getPegboardHoleCounts({
    width: target.bay.width,
    height: target.height,
    holesOffsetHorizontal: target.holesOffsetHorizontal,
    holesOffsetVertical: target.holesOffsetVertical,
    holesGapHorizontal: target.holesGapHorizontal,
    holesGapVertical: target.holesGapVertical,
  });
  if (
    !Number.isInteger(operation.target.holeNoLeft) ||
    !Number.isInteger(operation.target.holeNoTop) ||
    operation.target.holeNoLeft < 1 ||
    operation.target.holeNoLeft > counts.horizontal ||
    operation.target.holeNoTop < 1 ||
    operation.target.holeNoTop > counts.vertical
  ) {
    return { allowed: false, reason: "invalid-hole" };
  }
  for (const source of sources) {
    const anchor = source.holes[0];
    if (!anchor) return { allowed: false, reason: "invalid-hole" };
    const sourceCounts = getPegboardHoleCounts({
      width: source.pegboard.bay.width,
      height: source.pegboard.height,
      holesOffsetHorizontal: source.pegboard.holesOffsetHorizontal,
      holesOffsetVertical: source.pegboard.holesOffsetVertical,
      holesGapHorizontal: source.pegboard.holesGapHorizontal,
      holesGapVertical: source.pegboard.holesGapVertical,
    });
    if (
      source.holes.some(
        (hole) =>
          hole.holeNoLeft < 1 ||
          hole.holeNoLeft > sourceCounts.horizontal ||
          hole.holeNoTop < 1 ||
          hole.holeNoTop > sourceCounts.vertical,
      )
    ) {
      return { allowed: false, reason: "invalid-hole" };
    }
    const left = operation.target.holeNoLeft + anchor.holeNoLeft - activeAnchor.holeNoLeft;
    const top = operation.target.holeNoTop + anchor.holeNoTop - activeAnchor.holeNoTop;
    if (
      source.holes.some(
        (hole) =>
          hole.holeNoLeft + left - anchor.holeNoLeft < 1 ||
          hole.holeNoLeft + left - anchor.holeNoLeft > counts.horizontal ||
          hole.holeNoTop + top - anchor.holeNoTop < 1 ||
          hole.holeNoTop + top - anchor.holeNoTop > counts.vertical,
      )
    ) {
      return { allowed: false, reason: "invalid-hole" };
    }
  }
  if (isPegboardPlacementNoOp(pog, sources, target, operation.target, activeAnchor)) {
    return { allowed: false, reason: "no-op" };
  }

  const stationaryHoleKeys = new Set(
    pog.planogram
      .getAllPegboardPlacements()
      .filter((placement) => !sourceIds.includes(placement.uniqueId))
      .flatMap((placement) =>
        placement.holes.map((hole) =>
          getPegboardHoleKey(placement.pegboard.uniqueId, hole.holeNoLeft, hole.holeNoTop),
        ),
      ),
  );
  const leftDelta = operation.target.holeNoLeft - activeAnchor.holeNoLeft;
  const topDelta = operation.target.holeNoTop - activeAnchor.holeNoTop;
  const hasCollision = sources.some((source) =>
    source.holes.some((hole) =>
      stationaryHoleKeys.has(
        getPegboardHoleKey(target.uniqueId, hole.holeNoLeft + leftDelta, hole.holeNoTop + topDelta),
      ),
    ),
  );
  if (hasCollision) return { allowed: false, reason: "collision" };

  return { allowed: true, reason: "allowed" };
};

export const executePegboardPlacement = ({
  operation,
  pog,
  selection,
}: {
  readonly operation: PegboardPlacementOperation;
  readonly pog: Pog;
  readonly selection?: PlanogramSelection;
}): PlanogramMoveResult => {
  const capability = evaluatePegboardPlacement(pog, operation);
  const preservedSelection = reconcileSelection(
    selection ?? emptyPlanogramSelection,
    projectPlanogramScene(pog),
  );
  if (!capability.allowed) {
    return capability.reason === "no-op"
      ? { pog, reason: "no-op", selection: preservedSelection, status: "no-op" }
      : { pog, reason: capability.reason, selection: preservedSelection, status: "rejected" };
  }
  const nextPog = new Pog(pog.toNonCircular());
  const target = getPegboard(nextPog, operation.target.pegboardId);
  const sources = getPegboardPlacementSources(nextPog, operation);
  const active = sources.find(
    (placement) => placement.uniqueId === operation.source.activePlacementId,
  );
  const activeAnchor = active?.holes[0];
  if (!target || !activeAnchor || sources.length !== operation.source.placementIds.length) {
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }
  try {
    const resultIds: string[] = [];
    for (const source of sources) {
      const sourceAnchor = source.holes[0];
      if (!sourceAnchor)
        return {
          pog,
          reason: "partial-mutation",
          selection: preservedSelection,
          status: "rejected",
        };
      const leftDelta = operation.target.holeNoLeft - activeAnchor.holeNoLeft;
      const topDelta = operation.target.holeNoTop - activeAnchor.holeNoTop;
      const oldPegboard = source.pegboard;
      const oldIndex = oldPegboard.placements.findIndex(
        (placement) => placement.uniqueId === source.uniqueId,
      );
      if (oldIndex < 0)
        return {
          pog,
          reason: "partial-mutation",
          selection: preservedSelection,
          status: "rejected",
        };
      oldPegboard.placements.splice(oldIndex, 1);
      source.pegboard = target;
      source.holes = source.holes.map((hole) => ({
        ...hole,
        holeNoLeft: hole.holeNoLeft + leftDelta,
        holeNoTop: hole.holeNoTop + topDelta,
      }));
      target.placements.push(source);
      resultIds.push(source.uniqueId);
    }
    const committedSelection = reconcileSelection(
      {
        anchorId: operation.source.activePlacementId,
        focusedId: operation.source.activePlacementId,
        ids: resultIds,
        kind: "placement",
      },
      projectPlanogramScene(nextPog),
    );
    return { pog: nextPog, reason: "allowed", selection: committedSelection, status: "committed" };
  } catch (error) {
    console.error("Failed to execute pegboard placement operation", error);
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }
};

const isItemPlacementShelf = (item: PlanogramItem) =>
  item.shelf.type === "REGULAR" || item.shelf.type === "HANGCELL";

const getCanonicalSourceItems = (pog: Pog, operation: ItemPlacementOperation) => {
  const sourceIds = new Set(operation.source.itemIds);
  return pog.planogram.getAllItems().filter((item) => sourceIds.has(item.uniqueId));
};

export const hasValidItemGeometry = (item: {
  getBlockHeight: () => number;
  getBlockWidth: () => number;
  getHeight: () => number;
  getWidth: () => number;
  getXCappingWidth: () => number;
  getYCappingHeight: () => number;
}) => {
  const width = item.getWidth();
  const height = item.getHeight();
  const blockWidth = item.getBlockWidth();
  const blockHeight = item.getBlockHeight();
  const xCappingWidth = item.getXCappingWidth();
  const yCappingHeight = item.getYCappingHeight();

  return (
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0 &&
    Number.isFinite(blockWidth) &&
    blockWidth > 0 &&
    Number.isFinite(blockHeight) &&
    blockHeight > 0 &&
    Number.isFinite(xCappingWidth) &&
    Number.isFinite(yCappingHeight)
  );
};

export const hasValidSourceItemGeometry = (pog: Pog, itemIds: readonly string[]) =>
  itemIds.every((itemId) => {
    const item = pog.getItem(itemId);
    return item === null || hasValidItemGeometry(item);
  });

const getMergedItems = (item: PlanogramItem) =>
  item.shelf
    .getFirstShelf()
    .getMergedShelves()
    .flatMap((shelf) => shelf.items);

const isAdjacentInMergedGroup = (
  source: PlanogramItem,
  target: PlanogramItem,
  side: ItemPlacementOperation["target"]["side"],
) => {
  if (source.shelf.getFirstShelf().uniqueId !== target.shelf.getFirstShelf().uniqueId) {
    return false;
  }

  const mergedItems = getMergedItems(source);
  const sourceIndex = mergedItems.findIndex((item) => item.uniqueId === source.uniqueId);
  const targetIndex = mergedItems.findIndex((item) => item.uniqueId === target.uniqueId);

  return side === "left" ? sourceIndex === targetIndex - 1 : sourceIndex === targetIndex + 1;
};

const isBatchAdjacentInMergedGroup = (
  sources: readonly PlanogramItem[],
  target: PlanogramItem,
  side: ItemPlacementOperation["target"]["side"],
) => {
  if (sources.length === 0) return false;

  const firstSource = sources[0];
  if (
    !firstSource ||
    firstSource.shelf.getFirstShelf().uniqueId !== target.shelf.getFirstShelf().uniqueId
  ) {
    return false;
  }

  const mergedItems = getMergedItems(firstSource);
  const sourceIndices = sources
    .map((source) => mergedItems.findIndex((item) => item.uniqueId === source.uniqueId))
    .sort((left, right) => left - right);
  const targetIndex = mergedItems.findIndex((item) => item.uniqueId === target.uniqueId);

  if (sourceIndices.some((index) => index < 0)) return false;

  const contiguous = sourceIndices.every((index, position) => {
    const previousIndex = sourceIndices[position - 1];
    return position === 0 || (previousIndex !== undefined && index === previousIndex + 1);
  });

  if (!contiguous) return false;

  return side === "left"
    ? sourceIndices[sourceIndices.length - 1] === targetIndex - 1
    : sourceIndices[0] === targetIndex + 1;
};

export const evaluateStandardMove = (
  pog: Pog,
  operation: ItemPlacementOperation,
): StandardMoveCapability => {
  const sourceIds = operation.source.itemIds;

  if (sourceIds.length === 0) {
    return { allowed: false, reason: "empty-source" };
  }

  if (new Set(sourceIds).size !== sourceIds.length) {
    return { allowed: false, reason: "duplicate-source" };
  }

  if (!sourceIds.includes(operation.source.activeItemId)) {
    return { allowed: false, reason: "unsupported-source-set" };
  }

  const sources = getCanonicalSourceItems(pog, operation);
  if (sources.length !== sourceIds.length) {
    return { allowed: false, reason: "missing-source" };
  }

  if (sources.some((source) => !hasValidItemGeometry(source))) {
    return { allowed: false, reason: "invalid-source-dimensions" };
  }

  if (sources.some((source) => !isItemPlacementShelf(source))) {
    return { allowed: false, reason: "incompatible-source" };
  }

  const target = getShelfItem(pog, operation.target.itemId);
  if (!target) return { allowed: false, reason: "missing-target" };

  if (sourceIds.includes(target.uniqueId)) {
    return { allowed: false, reason: "self-target" };
  }

  if (!isItemPlacementShelf(target)) {
    return { allowed: false, reason: "incompatible-target" };
  }

  const singleSource = sources[0];
  const isNoOp =
    operation.intent === "move" &&
    (sources.length === 1
      ? singleSource !== undefined &&
        isAdjacentInMergedGroup(singleSource, target, operation.target.side)
      : isBatchAdjacentInMergedGroup(sources, target, operation.target.side));

  if (isNoOp) {
    return { allowed: false, reason: "no-op" };
  }

  return { allowed: true, reason: "allowed" };
};

const getPreservedSelection = (
  pog: Pog,
  selection: PlanogramSelection | undefined,
): PlanogramSelection =>
  reconcileSelection(selection ?? emptyPlanogramSelection, projectPlanogramScene(pog));

const getCommittedSelection = (
  pog: Pog,
  itemIds: readonly string[],
  activeItemId: string,
): PlanogramSelection =>
  reconcileSelection(
    {
      anchorId: activeItemId,
      focusedId: activeItemId,
      ids: itemIds,
      kind: "product",
    },
    projectPlanogramScene(pog),
  );

export const executeStandardMove = ({
  operation,
  pog,
  selection,
}: {
  readonly operation: ItemPlacementOperation;
  readonly pog: Pog;
  readonly selection?: PlanogramSelection;
}): StandardMoveResult => {
  const capability = evaluateStandardMove(pog, operation);

  if (!capability.allowed) {
    if (capability.reason === "no-op") {
      return {
        pog,
        reason: "no-op",
        selection: getPreservedSelection(pog, selection),
        status: "no-op",
      };
    }

    return {
      pog,
      reason: capability.reason,
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }

  const nextPog = new Pog(pog.toNonCircular());
  const sources = getCanonicalSourceItems(nextPog, operation);
  const target = getShelfItem(nextPog, operation.target.itemId);

  if (sources.length !== operation.source.itemIds.length || !target) {
    return {
      pog,
      reason: "commit-failed",
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }

  const activeIndex = sources.findIndex(
    (source) => source.uniqueId === operation.source.activeItemId,
  );

  if (activeIndex < 0) {
    return {
      pog,
      reason: "commit-failed",
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }

  try {
    const resultingItems =
      operation.intent === "copy"
        ? sources.map((source) => source.duplicate(target.shelf))
        : sources;
    const itemsForInsertion =
      operation.target.side === "left" ? [...resultingItems].reverse() : resultingItems;

    target.rangeItems(itemsForInsertion, operation.target.side);

    const resultingIds = resultingItems.map((item) => item.uniqueId);
    const activeResultId = resultingIds[activeIndex];

    if (!activeResultId) {
      return {
        pog,
        reason: "commit-failed",
        selection: getPreservedSelection(pog, selection),
        status: "rejected",
      };
    }

    return {
      pog: nextPog,
      reason: "allowed",
      selection: getCommittedSelection(nextPog, resultingIds, activeResultId),
      status: "committed",
    };
  } catch (error) {
    console.error("Failed to execute item placement operation", error);
    return {
      pog,
      reason: "commit-failed",
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }
};

const getShelfIds = (pog: Pog, operation: ShelfPlacementOperation | ShelfResizeOperation) =>
  operation.source.shelfIds.map((id) => pog.getShelf(id));

const hasCompleteMergedGroups = (
  pog: Pog,
  operation: ShelfPlacementOperation | ShelfResizeOperation,
) => {
  const requested = new Set(operation.source.shelfIds);
  return getShelfIds(pog, operation).every((shelf) => {
    if (!shelf) return false;
    return shelf.getMergedShelves().every((member) => requested.has(member.uniqueId));
  });
};

const evaluateShelfSource = (
  pog: Pog,
  operation: ShelfPlacementOperation | ShelfResizeOperation,
): PlanogramMoveCapability | null => {
  const shelfIds = operation.source.shelfIds;
  if (shelfIds.length === 0) return { allowed: false, reason: "empty-source" };
  if (new Set(shelfIds).size !== shelfIds.length) {
    return { allowed: false, reason: "duplicate-shelf" };
  }
  if (!shelfIds.includes(operation.source.activeShelfId)) {
    return { allowed: false, reason: "unsupported-source-set" };
  }
  const shelves = getShelfIds(pog, operation);
  if (shelves.some((shelf) => !shelf)) return { allowed: false, reason: "missing-shelf" };
  if (!hasCompleteMergedGroups(pog, operation)) {
    return { allowed: false, reason: "unsupported-movement" };
  }
  return null;
};

export const evaluateShelfPlacement = (
  pog: Pog,
  operation: ShelfPlacementOperation,
): PlanogramMoveCapability => {
  const sourceFailure = evaluateShelfSource(pog, operation);
  if (sourceFailure) return sourceFailure;
  const targetBay = pog.getBay(operation.target.bayId);
  if (!targetBay) return { allowed: false, reason: "missing-target" };
  if (
    !Number.isInteger(operation.target.notchNo) ||
    operation.target.notchNo < 1 ||
    operation.target.notchNo > targetBay.maxNotch
  ) {
    return { allowed: false, reason: "unsupported-movement" };
  }
  const sourceIds = new Set(operation.source.shelfIds);
  const sources = getShelfIds(pog, operation).filter((shelf) => shelf != null);
  const activeShelf = pog.getShelf(operation.source.activeShelfId);
  if (!activeShelf || activeShelf.bay.uniqueId !== targetBay.uniqueId) {
    return { allowed: false, reason: "unsupported-movement" };
  }
  const notchOffset = operation.target.notchNo - activeShelf.getNotchNo();
  for (const shelf of sources) {
    const targetNotch = shelf.getNotchNo() + notchOffset;
    if (targetNotch < 1 || targetNotch > shelf.bay.maxNotch) {
      return { allowed: false, reason: "unsupported-movement" };
    }
    const occupied = shelf.bay.shelves.some(
      (candidate) => !sourceIds.has(candidate.uniqueId) && candidate.getNotchNo() === targetNotch,
    );
    if (occupied) return { allowed: false, reason: "collision" };
  }
  if (notchOffset === 0) return { allowed: false, reason: "no-op" };
  return { allowed: true, reason: "allowed" };
};

export const evaluateShelfResize = (
  pog: Pog,
  operation: ShelfResizeOperation,
): PlanogramMoveCapability => {
  const sourceFailure = evaluateShelfSource(pog, operation);
  if (sourceFailure) return sourceFailure;
  const dimensions = operation.target.dimensions;
  if (
    Object.values(dimensions).length === 0 ||
    Object.values(dimensions).some(
      (value) => value !== undefined && (!Number.isFinite(value) || value <= 0),
    )
  ) {
    return { allowed: false, reason: "invalid-resize" };
  }
  const sources = getShelfIds(pog, operation).filter((shelf) => shelf != null);
  if (
    sources.some((shelf) => dimensions.width !== undefined && dimensions.width > shelf.bay.width)
  ) {
    return { allowed: false, reason: "collision" };
  }
  if (dimensions.width !== undefined) {
    const groups = new Map<string, { bayId: string; width: number }>();
    for (const shelf of sources) {
      const groupKey = `${shelf.getFirstShelf().uniqueId}:${shelf.bay.uniqueId}`;
      const group = groups.get(groupKey);
      if (group) {
        group.width += dimensions.width;
      } else {
        groups.set(groupKey, {
          bayId: shelf.bay.uniqueId,
          width: dimensions.width,
        });
      }
    }
    for (const group of groups.values()) {
      const bay = pog.getBay(group.bayId);
      if (bay && group.width > bay.width) return { allowed: false, reason: "collision" };
    }
  }
  const unchanged = sources.every(
    (shelf) =>
      (dimensions.width === undefined || shelf.width === dimensions.width) &&
      (dimensions.depth === undefined || shelf.depth === dimensions.depth) &&
      (dimensions.thickness === undefined || shelf.thickness === dimensions.thickness),
  );
  return unchanged ? { allowed: false, reason: "no-op" } : { allowed: true, reason: "allowed" };
};

const getShelfSelection = (
  pog: Pog,
  ids: readonly string[],
  activeShelfId: string,
): PlanogramSelection =>
  reconcileSelection(
    { anchorId: activeShelfId, focusedId: activeShelfId, ids, kind: "shelf" },
    projectPlanogramScene(pog),
  );

const executeShelfOperation = ({
  operation,
  pog,
  selection,
}: {
  operation: ShelfPlacementOperation | ShelfResizeOperation;
  pog: Pog;
  selection?: PlanogramSelection;
}): PlanogramMoveResult => {
  const capability =
    operation.kind === "shelf-placement"
      ? evaluateShelfPlacement(pog, operation)
      : evaluateShelfResize(pog, operation);
  const preservedSelection = reconcileSelection(
    selection ?? emptyPlanogramSelection,
    projectPlanogramScene(pog),
  );
  if (!capability.allowed) {
    return capability.reason === "no-op"
      ? { pog, reason: "no-op", selection: preservedSelection, status: "no-op" }
      : { pog, reason: capability.reason, selection: preservedSelection, status: "rejected" };
  }

  if (operation.kind === "shelf-placement") {
    const activeShelf = pog.getShelf(operation.source.activeShelfId);
    if (!activeShelf) {
      return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
    }

    const movedPog = moveShelvesInPog({
      notchOffset: operation.target.notchNo - activeShelf.getNotchNo(),
      pog,
      shelfIds: operation.source.shelfIds,
    });
    if (!movedPog) {
      return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
    }

    return {
      pog: movedPog,
      reason: "allowed",
      selection: getShelfSelection(
        movedPog,
        operation.source.shelfIds,
        operation.source.activeShelfId,
      ),
      status: "committed",
    };
  }

  const nextPog = new Pog(pog.toNonCircular());
  const nextShelves = operation.source.shelfIds.map((id) => nextPog.getShelf(id));
  if (nextShelves.some((shelf) => !shelf)) {
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }
  try {
    for (const shelf of nextShelves) {
      if (!shelf)
        return {
          pog,
          reason: "partial-mutation",
          selection: preservedSelection,
          status: "rejected",
        };
      const { dimensions } = operation.target;
      if (dimensions.width !== undefined) {
        shelf.merchandisableWidth = resizeShelfWidth(shelf, dimensions.width);
        shelf.deadSpans = resizeShelfDeadSpans(shelf.deadSpans, dimensions.width);
        shelf.width = dimensions.width;
      }
      if (dimensions.depth !== undefined) shelf.depth = dimensions.depth;
      if (dimensions.thickness !== undefined) shelf.thickness = dimensions.thickness;
    }
    return {
      pog: nextPog,
      reason: "allowed",
      selection: getShelfSelection(
        nextPog,
        operation.source.shelfIds,
        operation.source.activeShelfId,
      ),
      status: "committed",
    };
  } catch (error) {
    console.error("Failed to execute shelf resize operation", error);
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }
};

const getRangeSources = (pog: Pog, operation: ItemRangeOperation) =>
  operation.source.itemIds.map((itemId) => pog.getItem(itemId));

/**
 * Panel → canvas drop: unranged (or ranged) items land next to a target item
 * or at the end of a shelf — the DOM Dropper drop, expressed as an operation.
 */
export const evaluateItemRange = (
  pog: Pog,
  operation: ItemRangeOperation,
): PlanogramMoveCapability => {
  const sourceIds = operation.source.itemIds;
  if (sourceIds.length === 0) return { allowed: false, reason: "empty-source" };
  if (new Set(sourceIds).size !== sourceIds.length) {
    return { allowed: false, reason: "duplicate-source" };
  }
  if (!sourceIds.includes(operation.source.activeItemId)) {
    return { allowed: false, reason: "unsupported-source-set" };
  }
  const sources = getRangeSources(pog, operation);
  if (sources.some((source) => source === null)) {
    return { allowed: false, reason: "missing-source" };
  }
  if (sources.some((source) => source !== null && !hasValidItemGeometry(source))) {
    return { allowed: false, reason: "invalid-source-dimensions" };
  }

  if (operation.target.kind === "item") {
    const target = getShelfItem(pog, operation.target.itemId);
    if (!target) return { allowed: false, reason: "missing-target" };
    if (sourceIds.includes(target.uniqueId)) return { allowed: false, reason: "self-target" };
    if (!isItemPlacementShelf(target)) return { allowed: false, reason: "incompatible-target" };
    const rangedSources = sources.filter(
      (source): source is PlanogramItem => source instanceof PlanogramItem,
    );
    if (rangedSources.length === sources.length) {
      const side = operation.target.side;
      const singleSource = rangedSources[0];
      const isNoOp =
        rangedSources.length === 1
          ? singleSource !== undefined && isAdjacentInMergedGroup(singleSource, target, side)
          : isBatchAdjacentInMergedGroup(rangedSources, target, side);
      if (isNoOp) return { allowed: false, reason: "no-op" };
    }
    return { allowed: true, reason: "allowed" };
  }

  const shelf = pog.getShelf(operation.target.shelfId);
  if (!shelf) return { allowed: false, reason: "missing-target" };
  if (shelf.type !== "REGULAR" && shelf.type !== "HANGCELL") {
    return { allowed: false, reason: "incompatible-target" };
  }
  return { allowed: true, reason: "allowed" };
};

export const executeItemRange = ({
  operation,
  pog,
  selection,
}: {
  readonly operation: ItemRangeOperation;
  readonly pog: Pog;
  readonly selection?: PlanogramSelection;
}): PlanogramMoveResult => {
  const capability = evaluateItemRange(pog, operation);
  if (!capability.allowed) {
    if (capability.reason === "no-op") {
      return {
        pog,
        reason: "no-op",
        selection: getPreservedSelection(pog, selection),
        status: "no-op",
      };
    }
    return {
      pog,
      reason: capability.reason,
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }

  const nextPog = new Pog(pog.toNonCircular());
  const sources = getRangeSources(nextPog, operation);
  const resolvedSources = sources.filter(
    (source): source is NonNullable<typeof source> => source !== null,
  );
  if (resolvedSources.length !== operation.source.itemIds.length) {
    return {
      pog,
      reason: "commit-failed",
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }

  try {
    if (operation.target.kind === "item") {
      const target = getShelfItem(nextPog, operation.target.itemId);
      if (!target) {
        return {
          pog,
          reason: "commit-failed",
          selection: getPreservedSelection(pog, selection),
          status: "rejected",
        };
      }
      // Same mutation the DOM drop executes (usePogAtomActions.addItemNextToAnotherItem).
      target.rangeItems(resolvedSources, operation.target.side);
    } else {
      const shelf = nextPog.getShelf(operation.target.shelfId);
      if (!shelf) {
        return {
          pog,
          reason: "commit-failed",
          selection: getPreservedSelection(pog, selection),
          status: "rejected",
        };
      }
      // Same mutation the DOM shelf drop executes (usePogAtomActions.addItemsToShelf).
      shelf.addItems(resolvedSources);
    }

    return {
      pog: nextPog,
      reason: "allowed",
      selection: getCommittedSelection(
        nextPog,
        operation.source.itemIds,
        operation.source.activeItemId,
      ),
      status: "committed",
    };
  } catch (error) {
    console.error("Failed to execute item range operation", error);
    return {
      pog,
      reason: "commit-failed",
      selection: getPreservedSelection(pog, selection),
      status: "rejected",
    };
  }
};

const getContainer = (pog: Pog, containerId: string) =>
  pog.containers.find((container) => container.uniqueId === containerId);

const evaluateContainerBounds = (
  pog: Pog,
  operation: ContainerMoveOperation | ContainerResizeOperation,
  requireSameSize: boolean,
): PlanogramMoveCapability => {
  const container = getContainer(pog, operation.source.containerId);
  if (!container) return { allowed: false, reason: "missing-source" };

  const { bounds } = operation.target;
  if (
    !Number.isFinite(bounds.xLeft) ||
    !Number.isFinite(bounds.xRight) ||
    !Number.isFinite(bounds.yBottom) ||
    !Number.isFinite(bounds.yTop) ||
    bounds.xRight <= bounds.xLeft ||
    bounds.yTop <= bounds.yBottom
  ) {
    return { allowed: false, reason: "invalid-resize" };
  }

  // The DOM Draggable clamps to the planogram's outer bounds while dragging
  // (getOuterBounds); the policy rejects anything the clamp would forbid.
  if (
    bounds.xLeft < 0 ||
    bounds.yBottom < 0 ||
    bounds.xRight > pog.planogram.getWidth() ||
    bounds.yTop > pog.planogram.getHeight()
  ) {
    return { allowed: false, reason: "unsupported-movement" };
  }

  if (requireSameSize) {
    const sameWidth = bounds.xRight - bounds.xLeft === container.xRight - container.xLeft;
    const sameHeight = bounds.yTop - bounds.yBottom === container.yTop - container.yBottom;
    if (!sameWidth || !sameHeight) return { allowed: false, reason: "invalid-resize" };
  }

  const unchanged =
    bounds.xLeft === container.xLeft &&
    bounds.xRight === container.xRight &&
    bounds.yBottom === container.yBottom &&
    bounds.yTop === container.yTop;
  if (unchanged) return { allowed: false, reason: "no-op" };

  return { allowed: true, reason: "allowed" };
};

export const evaluateContainerMove = (
  pog: Pog,
  operation: ContainerMoveOperation,
): PlanogramMoveCapability => evaluateContainerBounds(pog, operation, true);

export const evaluateContainerResize = (
  pog: Pog,
  operation: ContainerResizeOperation,
): PlanogramMoveCapability => evaluateContainerBounds(pog, operation, false);

const executeContainerOperation = ({
  operation,
  pog,
  selection,
}: {
  readonly operation: ContainerMoveOperation | ContainerResizeOperation;
  readonly pog: Pog;
  readonly selection?: PlanogramSelection;
}): PlanogramMoveResult => {
  const capability =
    operation.kind === "container-move"
      ? evaluateContainerMove(pog, operation)
      : evaluateContainerResize(pog, operation);
  const preservedSelection = getPreservedSelection(pog, selection);
  if (!capability.allowed) {
    return capability.reason === "no-op"
      ? { pog, reason: "no-op", selection: preservedSelection, status: "no-op" }
      : { pog, reason: capability.reason, selection: preservedSelection, status: "rejected" };
  }

  const nextPog = new Pog(pog.toNonCircular());
  const container = getContainer(nextPog, operation.source.containerId);
  if (!container) {
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }

  try {
    const { bounds } = operation.target;
    container.xLeft = bounds.xLeft;
    container.xRight = bounds.xRight;
    container.yBottom = bounds.yBottom;
    container.yTop = bounds.yTop;

    // Same follow-up the DOM Containers.onModify performs.
    if (container.algoCurrentTargetWidth !== undefined) {
      container.algoCurrentTargetWidth = calculateContainerAlgoPoints(
        container,
        nextPog,
      ).currentTargetWidth;
    }

    return {
      pog: nextPog,
      reason: "allowed",
      selection: reconcileSelection(
        {
          anchorId: operation.source.containerId,
          focusedId: operation.source.containerId,
          ids: [operation.source.containerId],
          kind: "container",
        },
        projectPlanogramScene(nextPog),
      ),
      status: "committed",
    };
  } catch (error) {
    console.error("Failed to execute container operation", error);
    return { pog, reason: "partial-mutation", selection: preservedSelection, status: "rejected" };
  }
};

export const evaluateStandardOperation = (
  pog: Pog,
  operation: PlanogramOperation,
): PlanogramMoveCapability => {
  switch (operation.kind) {
    case "container-move":
      return evaluateContainerMove(pog, operation);
    case "container-resize":
      return evaluateContainerResize(pog, operation);
    case "item-placement":
      return evaluateStandardMove(pog, operation);
    case "item-range":
      return evaluateItemRange(pog, operation);
    case "pegboard-placement":
      return evaluatePegboardPlacement(pog, operation);
    case "shelf-placement":
      return evaluateShelfPlacement(pog, operation);
    case "shelf-resize":
      return evaluateShelfResize(pog, operation);
  }
};

export const executeStandardOperation = ({
  operation,
  pog,
  selection,
}: {
  readonly operation: PlanogramOperation;
  readonly pog: Pog;
  readonly selection?: PlanogramSelection;
}): PlanogramMoveResult => {
  if (operation.kind === "container-move" || operation.kind === "container-resize")
    return executeContainerOperation({ operation, pog, selection });
  if (operation.kind === "item-placement")
    return executeStandardMove({ operation, pog, selection });
  if (operation.kind === "item-range") return executeItemRange({ operation, pog, selection });
  if (operation.kind === "pegboard-placement")
    return executePegboardPlacement({ operation, pog, selection });
  return executeShelfOperation({ operation, pog, selection });
};

export const standardPolicy: PlanogramPolicy = {
  evaluate: evaluateStandardOperation,
  execute: executeStandardOperation,
};
